// =============================================================================
// useBookingFlow.ts — PAY-6 / CLINIO-202
// =============================================================================
// Hooks for the prepayment booking flow.
// - useCreateBookingIntent  : creates the booking_intent row in the DB
//                             (via the create-booking-intent Edge Function).
// - useRequestBookingPayLink: calls request_booking_pay_link(uuid) RPC which
//                             invokes pay-create-booking-checkout via pg_net.
// - useBookingIntentStatus  : polls get_booking_intent_status_via_edge to show
//                             "Waiting for confirmation..." after redirect-back.
// =============================================================================

import { useMutation, useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CreateBookingIntentInput {
  clinic_slug: string;
  service_id: string;
  clinician_id: string;
  start_time: string;  // ISO-8601
  patient: {
    first_name: string;
    last_name: string;
    email: string;
    phone?: string;
    notes?: string;
  };
}

export interface CreateBookingIntentResult {
  booking_intent_id: string;
  deposit_amount_cents: number;
  expires_at: string;
}

export interface BookingPayLinkResult {
  checkout_url: string;
  pay_token: string;
}

export interface BookingIntentStatus {
  status: 'pending_payment' | 'payment_submitted' | 'confirmed' | 'expired' | 'cancelled';
  expires_at: string;
  pay_token: string | null;
}

// ---------------------------------------------------------------------------
// useCreateBookingIntent
// ---------------------------------------------------------------------------

/**
 * Creates a booking_intent row for a prepayment-required service.
 * Calls the create-booking-intent Edge Function (public, no JWT).
 */
export function useCreateBookingIntent() {
  return useMutation({
    mutationFn: async (input: CreateBookingIntentInput): Promise<CreateBookingIntentResult> => {
      const { data, error } = await supabase.functions.invoke<{
        ok: true;
        data: CreateBookingIntentResult;
      }>('create-booking-intent', {
        method: 'POST',
        body: input,
      });
      if (error) throw error;
      if (!data?.ok) {
        const apiError = (data as unknown as { error: { code: string; message: string } })?.error;
        const err = new Error(apiError?.message ?? 'Failed to create booking intent') as Error & {
          code?: string;
        };
        err.code = apiError?.code;
        throw err;
      }
      return data.data;
    },
  });
}

// ---------------------------------------------------------------------------
// useRequestBookingPayLink
// ---------------------------------------------------------------------------

/**
 * Calls the request_booking_pay_link(uuid) SQL RPC which invokes
 * pay-create-booking-checkout via pg_net and returns { checkout_url, pay_token }.
 * Callable by anonymous users (booking flow is public).
 */
export function useRequestBookingPayLink() {
  return useMutation({
    mutationFn: async (bookingIntentId: string): Promise<BookingPayLinkResult> => {
      const { data, error } = await supabase.rpc('request_booking_pay_link', {
        p_booking_intent_id: bookingIntentId,
      });
      if (error) {
        const msg = (error as { message?: string }).message ?? 'Could not create pay link';
        const err = new Error(msg) as Error & { code?: string };
        // Map SQLSTATE to a user-visible code.
        if (msg.includes('not-found') || msg.includes('P0002')) err.code = 'not-found';
        else if (msg.includes('gone') || msg.includes('expired')) err.code = 'gone';
        else if (msg.includes('invalid-input')) err.code = 'invalid-input';
        else err.code = 'internal-error';
        throw err;
      }
      // data is JSONB: { checkout_url, pay_token }
      const result = data as BookingPayLinkResult | null;
      if (!result?.checkout_url || !result?.pay_token) {
        throw new Error('Incomplete pay link response from server');
      }
      return result;
    },
  });
}

// ---------------------------------------------------------------------------
// useBookingIntentStatus
// ---------------------------------------------------------------------------

/**
 * Polls the booking_intent status after the patient returns from the
 * Clinio Pay hosted checkout page.
 * Uses get_booking_intent_status_via_edge via service_role (called from EF).
 *
 * TODO (PAY-5 dependency): when pay-checkout-status endpoint is fully live,
 * this hook should prefer polling that endpoint for richer status data.
 * Currently falls back to local DB status via get_booking_intent_status_via_edge.
 */
export function useBookingIntentStatus(
  bookingIntentId: string | null,
  options?: { enabled?: boolean; refetchInterval?: number },
) {
  const enabled = (options?.enabled ?? true) && Boolean(bookingIntentId);
  const refetchInterval = options?.refetchInterval ?? 3_000;

  return useQuery({
    queryKey: ['booking-intent-status', bookingIntentId] as const,
    enabled,
    refetchInterval: (query) => {
      // Stop polling once confirmed or in a terminal state.
      const status = (query.state.data as BookingIntentStatus | undefined)?.status;
      if (status === 'confirmed' || status === 'expired' || status === 'cancelled') {
        return false;
      }
      return refetchInterval;
    },
    queryFn: async (): Promise<BookingIntentStatus> => {
      if (!bookingIntentId) throw new Error('bookingIntentId is required');

      const { data, error } = await supabase.functions.invoke<{
        ok: true;
        data: BookingIntentStatus;
      }>('get-booking-intent-status', {
        method: 'POST',
        body: { booking_intent_id: bookingIntentId },
      });

      if (error || !data?.ok) {
        // Fallback: optimistically return 'payment_submitted' so the UI keeps spinning.
        return { status: 'payment_submitted', expires_at: '', pay_token: null };
      }
      return data.data;
    },
    staleTime: 0,
    retry: false,
  });
}
